import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { HeartHandshake, ArrowLeft, ExternalLink } from 'lucide-react';
import { api } from '../lib/api';
import { toast } from 'sonner';

const STATUSES = [['not_started', 'Not Started'], ['researching', 'Researching'], ['preparing_docs', 'Preparing Documents'], ['submitted', 'Submitted'], ['under_review', 'Under Review'], ['approved', 'Approved'], ['denied', 'Denied']];

export default function Helping() {
  const { ownerId } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const load = () => api.get(`/helpers/${ownerId}/applications`).then((r) => setD(r.data)).catch((e) => setErr(e?.response?.data?.detail || 'No access'));
  useEffect(() => { load(); }, [ownerId]);

  const setStatus = async (a, status) => {
    try { await api.post(`/helpers/${ownerId}/applications/${a.app_id}/status`, { status }); toast.success('Updated'); load(); } catch { toast.error('Could not update'); }
  };

  if (err) return <div className="max-w-3xl mx-auto px-4 py-16 text-center" data-testid="helping-denied"><h1 className="text-2xl font-bold">{err}</h1><Link to="/applications" className="text-primary underline text-sm mt-2 inline-block">Back</Link></div>;
  if (!d) return <div className="max-w-4xl mx-auto px-4 py-16 text-muted-foreground">Loading…</div>;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10" data-testid="helping-page">
      <Link to="/applications" className="text-sm text-primary inline-flex items-center gap-1 mb-4"><ArrowLeft className="w-4 h-4" /> My own tracker</Link>
      <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#C26227]/10 text-[#C26227] text-xs font-medium mb-3 ml-4"><HeartHandshake className="w-3.5 h-3.5" /> Helper view</div>
      <h1 className="text-3xl font-bold" style={{fontFamily: 'Lexend'}}>{d.owner_name || 'Their'} applications</h1>
      <p className="text-muted-foreground mt-2">You can update statuses and next steps. Documents and private notes stay hidden. Every change is recorded as made by you.</p>
      <div className="grid md:grid-cols-2 gap-4 mt-6">
        {d.applications.length === 0 && <Card className="p-6 bg-card text-muted-foreground">Nothing tracked yet.</Card>}
        {d.applications.map((a) => (
          <Card key={a.app_id} className="p-5 bg-card" data-testid={`helping-app-${a.program_id}`}>
            <Badge variant="outline" className="mb-2">{a.program_category}</Badge>
            <h3 className="font-bold text-lg" style={{fontFamily: 'Lexend'}}>{a.program_name}</h3>
            {a.next_action && <p className="text-sm mt-1"><span className="text-muted-foreground">Next:</span> {a.next_action}</p>}
            {a.target_date && <p className="text-xs text-muted-foreground mt-1">Target date {a.target_date}</p>}
            <div className="mt-3 flex items-center gap-2">
              <Select value={a.status} onValueChange={(v) => setStatus(a, v)}>
                <SelectTrigger className="h-10 w-56" data-testid={`helping-status-${a.program_id}`}><SelectValue /></SelectTrigger>
                <SelectContent className="bg-card">{STATUSES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
              </Select>
              {a.status_check && <Button asChild size="sm" variant="ghost"><a href={a.status_check.url} target="_blank" rel="noopener noreferrer">{a.status_check.portal} <ExternalLink className="w-3 h-3 ml-1" /></a></Button>}
            </div>
            {a.updated_by_helper && <p className="text-[10px] text-muted-foreground mt-2">Last updated by helper {a.updated_by_helper}</p>}
          </Card>
        ))}
      </div>
    </div>
  );
}
